var __defProp = Object.defineProperty;
var __export = (target, all) => {
  for (var name in all)
    __defProp(target, name, { get: all[name], enumerable: true });
};

// src/dateTime.ts
var dateTime_exports = {};
__export(dateTime_exports, {
  addDay: () => addDay,
  dayDifference: () => dayDifference,
  dayFormatter: () => dayFormatter,
  dayWrittenMonthFormatter: () => dayWrittenMonthFormatter,
  formatSecondsToTime: () => formatSecondsToTime,
  fullFormatter: () => fullFormatter,
  fullFormatterCurrentTimezone: () => fullFormatterCurrentTimezone,
  getTimezone: () => getTimezone,
  hotelDateNow: () => hotelDateNow,
  isSameDay: () => isSameDay,
  setCurrentTimezone: () => setCurrentTimezone,
  shortFormatter: () => shortFormatter,
  timeFormatter: () => timeFormatter,
  timeToSeconds: () => timeToSeconds,
  toDay: () => toDay,
  toDuration: () => toDuration,
  toRange: () => toRange,
  toTime: () => toTime,
  toUtcDateString: () => toUtcDateString,
  toUtcTimeString: () => toUtcTimeString
});
var currentTimezone;
function setCurrentTimezone(timezone) {
  currentTimezone = timezone;
}
function getTimezone() {
  if (!currentTimezone) {
    console.warn(
      "No current timezone was set! Defaulting to UTC. Call setCurrentTimezone() with the hotel timezone."
    );
    return "UTC";
  }
  return currentTimezone;
}
var dayFormatter = {
  format: (date) => new Intl.DateTimeFormat(void 0, {
    month: "2-digit",
    day: "2-digit",
    timeZone: getTimezone()
  }).format(date)
};
var dayWrittenMonthFormatter = {
  format: (date) => new Intl.DateTimeFormat(void 0, {
    month: "long",
    day: "2-digit",
    timeZone: getTimezone()
  }).format(date)
};
var timeFormatter = {
  format: (date) => new Intl.DateTimeFormat(void 0, {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: getTimezone()
  }).format(date)
};
var shortFormatter = {
  format: (date) => new Intl.DateTimeFormat(void 0, {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: getTimezone()
  }).format(date)
};
var fullFormatter = {
  format: (date) => new Intl.DateTimeFormat(void 0, {
    weekday: "short",
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: getTimezone()
  }).format(date)
};
var fullFormatterCurrentTimezone = new Intl.DateTimeFormat(void 0, {
  weekday: "short",
  year: "2-digit",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit"
});
function hotelDateNow() {
  const now = /* @__PURE__ */ new Date();
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: getTimezone(),
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false
  }).formatToParts(now);
  const get = (type) => parseInt(parts.find((p) => p.type === type).value);
  return new NaiveDateTime({
    year: get("year"),
    month: get("month"),
    day: get("day"),
    hours: get("hour"),
    minutes: get("minute"),
    seconds: get("second")
  });
}
var dayInMilliseconds = 1e3 * 60 * 60 * 24;
function toRange({
  duration,
  isoDateTime,
  showAsOffset = false,
  hideStartDate = false,
  showShortDate = false,
  dayLabelTranslation = (count) => `${count} day`
}) {
  const startDate = new Date(isoDateTime);
  const dateFormatter = showShortDate ? shortFormatter : fullFormatter;
  const startFormatter = hideStartDate ? timeFormatter : dateFormatter;
  const startText = startFormatter.format(startDate);
  const endText = (() => {
    if (!duration) return "";
    const endTime = new Date(startDate.getTime() + duration);
    if (isSameDay(startDate, endTime)) {
      return ` - ${timeFormatter.format(endTime)}`;
    }
    if (showAsOffset) {
      const startDay = toDay(startDate);
      const endDay = toDay(endTime);
      const diffTime = Math.abs(endDay.getTime() - startDay.getTime());
      const diffDays = Math.ceil(diffTime / (1e3 * 60 * 60 * 24));
      return ` - ${timeFormatter.format(endTime)} (+${dayLabelTranslation(diffDays)})`;
    }
    return ` - ${dateFormatter.format(endTime)}`;
  })();
  return `${startText}${endText}`;
}
function toTime({ isoDateTime }) {
  const startDate = new Date(isoDateTime);
  const startTime = timeFormatter.format(startDate);
  return startTime;
}
function toUtcTimeString(date) {
  const hours = `${date.getUTCHours()}`.padStart(2, "0");
  const minutes = `${date.getUTCMinutes()}`.padStart(2, "0");
  return `${hours}:${minutes}`;
}
function toUtcDateString(date) {
  const years = `${date.getUTCFullYear()}`;
  const months = `${date.getUTCMonth() + 1}`.padStart(2, "0");
  const days = `${date.getUTCDate()}`.padStart(2, "0");
  return `${years}-${months}-${days}`;
}
function isSameDay(d1, d2) {
  return d1.getUTCFullYear() === d2.getUTCFullYear() && d1.getUTCMonth() === d2.getUTCMonth() && d1.getUTCDate() === d2.getUTCDate();
}
function addDay(date, days = 1) {
  const newDate = date.getTime() + days * 24 * 60 * 60 * 1e3;
  return new Date(newDate);
}
function toDay(date) {
  const newDate = /* @__PURE__ */ new Date();
  newDate.setFullYear(date.getFullYear());
  newDate.setMonth(date.getMonth());
  newDate.setDate(date.getDate());
  newDate.setHours(0, 0, 0, 0);
  return newDate;
}
function dayDifference(date1, date2) {
  const day1 = toDay(date1).getTime();
  const day2 = toDay(date2).getTime();
  const diff = day2 - day1;
  return diff / dayInMilliseconds;
}
function toDuration(start, end) {
  return end.getTime() - start.getTime();
}
function formatSecondsToTime(seconds) {
  seconds = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  const formattedMinutes = String(minutes).padStart(2, "0");
  const formattedSeconds = String(remainingSeconds).padStart(2, "0");
  return `${formattedMinutes}:${formattedSeconds}`;
}
function timeToSeconds(timeString) {
  const timeRegex = /^([0-5][0-9]):([0-5][0-9])$/;
  if (!timeRegex.test(timeString)) {
    throw new Error("Invalid time format. Please use MM:SS");
  }
  const [targetMinutes, targetSeconds] = timeString.split(":").map(Number);
  return targetMinutes * 60 + targetSeconds;
}

// src/naiveDateTime.ts
var TimeUnits = /* @__PURE__ */ ((TimeUnits2) => {
  TimeUnits2["MILLISECOND"] = "millisecond";
  TimeUnits2["SECOND"] = "second";
  TimeUnits2["MINUTE"] = "minute";
  TimeUnits2["HOUR"] = "hour";
  TimeUnits2["DAY"] = "day";
  TimeUnits2["MONTH"] = "month";
  TimeUnits2["YEAR"] = "year";
  return TimeUnits2;
})(TimeUnits || {});
var NaiveDateTime = class _NaiveDateTime {
  constructor({
    year,
    month,
    day,
    hours,
    minutes,
    seconds
  }) {
    this.year = year;
    this.month = month ?? 1;
    this.day = day ?? 1;
    this.hours = hours ?? 0;
    this.minutes = minutes ?? 0;
    this.seconds = seconds ?? 0;
  }
  // --- Static Factories ---
  /**
   * Creates a NaiveDateTime from a standard JS Date using its UTC properties.
   */
  static fromDate(date) {
    return new _NaiveDateTime({
      year: date.getUTCFullYear(),
      month: date.getUTCMonth() + 1,
      day: date.getUTCDate(),
      hours: date.getUTCHours(),
      minutes: date.getUTCMinutes(),
      seconds: date.getUTCSeconds()
    });
  }
  /**
   * Translates a moment in time (Date) into the "Wall Clock" digits
   * seen in a specific timezone.
   * Useful for: "What time is it right now at the Hotel?"
   */
  static fromDateInTimezone(date, timezone = getTimezone()) {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false
    }).formatToParts(date);
    const get = (type) => parseInt(parts.find((p) => p.type === type).value);
    return new _NaiveDateTime({
      year: get("year"),
      month: get("month"),
      day: get("day"),
      hours: get("hour"),
      minutes: get("minute"),
      seconds: get("second")
    });
  }
  /**
   * Standard ISO string parser.
   * Note: If string has no 'Z', JS Date() might assume system local time.
   */
  static fromISO(iso) {
    return _NaiveDateTime.fromDate(new Date(iso));
  }
  /**
   * Strictly parses "Wall Clock" strings like "2025-09-01T20:00:00".
   * Bypasses JS Date parsing to ensure the time digits are never
   * shifted by the system's local timezone.
   */
  static fromNaiveISO(iso) {
    const match = iso.match(
      /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):?(\d{2})?/
    );
    if (!match) {
      throw new Error(`Invalid Naive ISO string format: ${iso}`);
    }
    const [, year, month, day, hours, minutes, seconds] = match;
    return new _NaiveDateTime({
      year: parseInt(year),
      month: parseInt(month),
      day: parseInt(day),
      hours: parseInt(hours),
      minutes: parseInt(minutes),
      seconds: seconds ? parseInt(seconds) : 0
    });
  }
  static fromEpoch(epoch) {
    return _NaiveDateTime.fromDate(new Date(epoch));
  }
  // --- Converters ---
  /**
   * Returns a UTC Date object where the UTC time matches the wall-clock time.
   * (e.g. 09:00 Wall Clock -> 09:00 UTC)
   */
  toDate() {
    return new Date(
      Date.UTC(
        this.year,
        this.month - 1,
        this.day,
        this.hours,
        this.minutes,
        this.seconds
      )
    );
  }
  /**
   * The Inverse operation: Takes the wall-clock digits and finds the
   * actual moment in time (UTC) they represent in the target timezone.
   */
  toZonedDate(timezone = getTimezone()) {
    const utcFromNaive = Date.UTC(
      this.year,
      this.month - 1,
      this.day,
      this.hours,
      this.minutes,
      this.seconds
    );
    const getOffset = (baseMillis, tz) => {
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: tz,
        hourCycle: "h23",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit"
      }).formatToParts(baseMillis);
      const d = (type) => parseInt(parts.find((p) => p.type === type).value);
      return Date.UTC(
        d("year"),
        d("month") - 1,
        d("day"),
        d("hour"),
        d("minute"),
        d("second")
      );
    };
    const diff = utcFromNaive - getOffset(utcFromNaive, timezone);
    return new Date(utcFromNaive + diff);
  }
  // --- Manipulation ---
  /**
   * Returns a new instance set to the start of the current day (00:00:00).
   */
  toDay() {
    return new _NaiveDateTime({
      year: this.year,
      month: this.month,
      day: this.day
    });
  }
  /**
   * Returns a new instance set to the first day of the month.
   */
  toMonth() {
    return new _NaiveDateTime({
      year: this.year,
      month: this.month
    });
  }
  isSameDay(others) {
    const day1 = this.toDay();
    const day2 = others.toDay();
    return day1.diff(day2, "millisecond" /* MILLISECOND */) === 0;
  }
  /**
   * Updates the date component while preserving the current time.
   * Input format: "YYYY-MM-DD"
   */
  setDate(date) {
    const [year, month, day] = date.split("-").map((val) => parseInt(val));
    return new _NaiveDateTime({ ...this, year, month, day });
  }
  /**
   * Updates the time component while preserving the current date.
   * Input format: "HH:mm" or "HH:mm:ss"
   */
  setTime(time) {
    const timeSplit = time.split(":").map((val) => parseInt(val));
    const [hours, minutes, seconds] = timeSplit.length === 3 ? timeSplit : [...timeSplit, 0];
    return new _NaiveDateTime({ ...this, hours, minutes, seconds });
  }
  /**
   * Adds or subtracts time units. Handles month/year overflows correctly
   * using the native UTC Date logic.
   */
  add(duration, unit) {
    const date = this.toDate();
    switch (unit) {
      case "year" /* YEAR */:
        date.setUTCFullYear(date.getUTCFullYear() + duration);
        break;
      case "month" /* MONTH */:
        date.setUTCMonth(date.getUTCMonth() + duration);
        break;
      case "day" /* DAY */:
        date.setUTCDate(date.getUTCDate() + duration);
        break;
      case "hour" /* HOUR */:
        date.setUTCHours(date.getUTCHours() + duration);
        break;
      case "minute" /* MINUTE */:
        date.setUTCMinutes(date.getUTCMinutes() + duration);
        break;
      case "second" /* SECOND */:
        date.setUTCSeconds(date.getUTCSeconds() + duration);
        break;
      case "millisecond" /* MILLISECOND */:
        date.setUTCMilliseconds(date.getUTCMilliseconds() + duration);
        break;
    }
    return _NaiveDateTime.fromDate(date);
  }
  /**
   * Returns the difference between two Naive dates.
   */
  diff(other, unit) {
    const ms = this.valueOf() - other.valueOf();
    switch (unit) {
      case "millisecond" /* MILLISECOND */:
        return ms;
      case "second" /* SECOND */:
        return Math.round(ms / 1e3);
      case "minute" /* MINUTE */:
        return Math.round(ms / (1e3 * 60));
      case "hour" /* HOUR */:
        return Math.round(ms / (1e3 * 60 * 60));
      case "day" /* DAY */:
        return Math.round(ms / (1e3 * 60 * 60 * 24));
      case "month" /* MONTH */:
        return (this.year - other.year) * 12 + (this.month - other.month);
      case "year" /* YEAR */:
        return this.year - other.year;
    }
  }
  /**
   * Rounds the current time up to the next nearest interval of the specified unit.
   * Only supports fixed-length units: Millisecond, Second, Minute, Hour, Day.
   * Example: 16:22.ceil(TimeUnits.MINUTE, 15) -> 16:30
   */
  ceil(unit, precision = 1) {
    const msPerUnit = {
      ["millisecond" /* MILLISECOND */]: 1,
      ["second" /* SECOND */]: 1e3,
      ["minute" /* MINUTE */]: 1e3 * 60,
      ["hour" /* HOUR */]: 1e3 * 60 * 60,
      ["day" /* DAY */]: 1e3 * 60 * 60 * 24
    };
    const unitInMs = msPerUnit[unit];
    if (!unitInMs) {
      return this;
    }
    const interval = unitInMs * precision;
    const currentMs = this.valueOf();
    const roundedMs = Math.ceil(currentMs / interval) * interval;
    return _NaiveDateTime.fromEpoch(roundedMs);
  }
  // --- Formatting ---
  /**
   * Format used by RRule DTSTART.
   * Strips separators and the 'Z'.
   * Example: 20250901T200000
   */
  getDtStartFormat() {
    return this.toISOString().replace(/[-:]/g, "");
  }
  /**
   * ISO string in UTC format.
   */
  getFull() {
    return `${this.getDate()}T${this.getTime(true)}Z`;
  }
  /**
   * Returns YYYY-MM-DD
   */
  getDate() {
    const month = `${this.month}`.padStart(2, "0");
    const day = `${this.day}`.padStart(2, "0");
    return `${this.year}-${month}-${day}`;
  }
  /**
   * Returns HH:mm or HH:mm:ss
   */
  getTime(withSeconds = false) {
    const hours = `${this.hours}`.padStart(2, "0");
    const minutes = `${this.minutes}`.padStart(2, "0");
    const seconds = `${this.seconds}`.padStart(2, "0");
    const base = `${hours}:${minutes}`;
    return withSeconds ? `${base}:${seconds}` : base;
  }
  equals(other) {
    return this.valueOf() === other.valueOf();
  }
  /**
   * Returns an ISO-8601 string without a timezone suffix.
   * Example: "2025-09-01T20:00:00"
   * This is what RRule and Wall-Clock systems expect.
   */
  toISOString() {
    return `${this.getDate()}T${this.getTime(true)}`;
  }
  /**
   * The default string representation of the object.
   * Now returns the naive ISO string (no 'Z').
   */
  toString() {
    return this.toISOString();
  }
  /**
   * Returns the ISO string with a 'Z' suffix for systems that
   * strictly require a UTC-formatted string.
   */
  toUTCISO() {
    return `${this.toISOString()}Z`;
  }
  /**
   * Returns the epoch milliseconds.
   * Crucial for comparisons (>, <, ===).
   */
  valueOf() {
    return this.toDate().getTime();
  }
  /**
   * Localized format using UTC as the reference zone to preserve wall-clock digits.
   */
  format(options) {
    return new Intl.DateTimeFormat(void 0, {
      ...options,
      timeZone: "UTC"
    }).format(this.toDate());
  }
  formatAsDay() {
    return this.format({ month: "2-digit", day: "2-digit" });
  }
  formatAsDayInMonth() {
    return this.format({ month: "long", day: "2-digit" });
  }
  formatAsFull() {
    return this.format({
      weekday: "short",
      year: "2-digit",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit"
    });
  }
  /**
   * Returns the day of the week.
   * 0 = Sunday, 1 = Monday, 2 = Tuesday, 3 = Wednesday,
   * 4 = Thursday, 5 = Friday, 6 = Saturday
   */
  getDayOfWeek() {
    return this.toDate().getUTCDay();
  }
};

// src/recurences.ts
var recurences_exports = {};
__export(recurences_exports, {
  currentOccurence: () => currentOccurence,
  currentOrNextOccurence: () => currentOrNextOccurence,
  nextOccurence: () => nextOccurence,
  nextOccurences: () => nextOccurences,
  occurencesForDay: () => occurencesForDay,
  occurencesForMonth: () => occurencesForMonth,
  rangesBetweenOccurences: () => rangesBetweenOccurences,
  recurrenceToRRule: () => recurrenceToRRule,
  recurrencesToRRule: () => recurrencesToRRule,
  rruleToApiReadyString: () => rruleToApiReadyString
});
import { rrulestr } from "rrule";
function recurrenceToRRule(recurrence) {
  const date = recurrence.startDate ? NaiveDateTime.fromNaiveISO(recurrence.startDate) : dateTime_exports.hotelDateNow();
  return {
    duration: recurrence.duration,
    rrule: rrulestr(
      `DTSTART:${date.getDtStartFormat()}
RRULE:${recurrence.rrule}`
    )
  };
}
function rruleToApiReadyString(rrule) {
  return rrule.toString().replace("RRULE:", "");
}
function recurrencesToRRule(recurrences) {
  return recurrences.map(recurrenceToRRule);
}
function occurenceToDateRange(start, duration) {
  const naiveStart = NaiveDateTime.fromDate(start);
  return {
    start: naiveStart,
    end: naiveStart.add(duration, "millisecond" /* MILLISECOND */),
    duration
  };
}
function occurencesForMonth(rrule, year, month) {
  const rules = Array.isArray(rrule) ? rrule : [rrule];
  const start = new NaiveDateTime({ year, month });
  const end = new NaiveDateTime({ year, month: month + 1 }).toDate();
  return rules.flatMap((rule) => {
    const offsetStart = start.add(rule.duration * -1, "millisecond" /* MILLISECOND */).toDate();
    return rule.rrule.between(offsetStart, end).map((start2) => occurenceToDateRange(start2, rule.duration));
  });
}
function occurencesForDay(rrule, day) {
  const rules = Array.isArray(rrule) ? rrule : [rrule];
  const start = day.toDay();
  const end = start.add(1, "day" /* DAY */).toDate();
  return rules.flatMap((rule) => {
    const offsetStart = start.add(rule.duration * -1, "millisecond" /* MILLISECOND */).toDate();
    return rule.rrule.between(offsetStart, end).map((start2) => occurenceToDateRange(start2, rule.duration));
  });
}
function currentOccurence(rrule, date) {
  const rules = Array.isArray(rrule) ? rrule : [rrule];
  const startDate = date ?? dateTime_exports.hotelDateNow();
  const end = startDate.toDate();
  return rules.reduce(function(next, rule) {
    const startWithOffset = startDate.add(rule.duration * -1, "millisecond" /* MILLISECOND */).toDate();
    const currentOccurences = rule.rrule.between(startWithOffset, end);
    const lastOccurence = currentOccurences[currentOccurences.length - 1];
    const currentRange = lastOccurence ? occurenceToDateRange(lastOccurence, rule.duration) : null;
    if (!next) return currentRange;
    if (currentRange && next.start > currentRange.start) return currentRange;
    return next;
  }, null);
}
function currentOrNextOccurence(rrule, date) {
  const rules = Array.isArray(rrule) ? rrule : [rrule];
  const startDate = date ?? dateTime_exports.hotelDateNow();
  return rules.reduce(function(next, rule) {
    const startWithOffset = startDate.add(rule.duration * -1, "millisecond" /* MILLISECOND */).toDate();
    const nextOccurence2 = rule.rrule.after(startWithOffset);
    const nextRange = nextOccurence2 ? occurenceToDateRange(nextOccurence2, rule.duration) : null;
    if (!next) return nextRange;
    if (nextRange && next.start > nextRange.start) return nextRange;
    return next;
  }, null);
}
function nextOccurence(rrule, date) {
  const rules = Array.isArray(rrule) ? rrule : [rrule];
  const startDate = date ?? dateTime_exports.hotelDateNow();
  const start = startDate.toDate();
  return rules.reduce(function(next, rule) {
    const nextOccurence2 = rule.rrule.after(start);
    const nextRange = nextOccurence2 ? occurenceToDateRange(nextOccurence2, rule.duration) : null;
    if (!next) return nextRange;
    if (nextRange && next.start > nextRange.start) return nextRange;
    return next;
  }, null);
}
function nextOccurences(rrule, count, prevDate) {
  const occurences = [];
  let lastDate = prevDate;
  for (let index = 0; index < count; index++) {
    const occurence = nextOccurence(rrule, lastDate);
    if (!occurence) return occurences;
    occurences.push(occurence);
    lastDate = occurence.start;
  }
  return occurences;
}
function rangesBetweenOccurences(rrule, start, end) {
  const rules = Array.isArray(rrule) ? rrule : [rrule];
  const endAsDate = end.toDate();
  return rules.flatMap(function(rule) {
    const startWithOffset = start.add(rule.duration * -1, "millisecond" /* MILLISECOND */).toDate();
    const occurences = rule.rrule.between(startWithOffset, endAsDate, true);
    return occurences.map(function(occ) {
      const naiveOcc = NaiveDateTime.fromDate(occ);
      const occEnd = naiveOcc.add(rule.duration, "millisecond" /* MILLISECOND */);
      const startDate = naiveOcc > start ? naiveOcc : start;
      const endDate = end > occEnd ? occEnd : end;
      const duration = endDate.diff(startDate, "millisecond" /* MILLISECOND */);
      return {
        start: startDate,
        end: startDate.add(duration, "millisecond" /* MILLISECOND */),
        duration
      };
    });
  }, null).toSorted((a, b) => a.start.valueOf() - b.start.valueOf());
}

// src/goodie.ts
var goodie_exports = {};
__export(goodie_exports, {
  formatAsPrice: () => formatAsPrice,
  isBookableFromGoodieFragment: () => isBookableFromGoodieFragment,
  setCurrentCurrency: () => setCurrentCurrency,
  toCurrentAvailabilityFromGoodieFragment: () => toCurrentAvailabilityFromGoodieFragment,
  toDuration: () => toDuration2,
  toGoodieType: () => toGoodieType,
  toInfo: () => toInfo,
  toInfoFromGoodieFragment: () => toInfoFromGoodieFragment,
  toNextOccurenceFromGoodieFragment: () => toNextOccurenceFromGoodieFragment,
  toNextOccurenceFromRecurrence: () => toNextOccurenceFromRecurrence,
  toPrice: () => toPrice,
  toPriceFromGoodieFragment: () => toPriceFromGoodieFragment
});
import { SharedAPI, getFragment, GraphQL } from "@guestgoodie/graphql";
var currentCurrency;
function setCurrentCurrency(currency) {
  currentCurrency = currency;
}
var toDuration2 = function(duration) {
  const minutes = Math.round(duration / 6e4);
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (hours === 0) {
    return `${remainingMinutes}m`;
  } else if (remainingMinutes === 0) {
    return `${hours}h`;
  } else {
    return `${hours}h ${remainingMinutes}m`;
  }
};
var toInfo = function({
  duration,
  location
}) {
  const final = [];
  if (location) final.push(location);
  if (duration) final.push(toDuration2(duration));
  return final.join(" | ");
};
var toPrice = function({
  price: priceCents,
  zeroText = void 0
}) {
  const price = (priceCents ?? 0) / 100;
  if (price === 0) return zeroText;
  return formatAsPrice(price);
};
function formatAsPrice(price) {
  if (!currentCurrency) {
    console.error(
      "[helpers/src/goodie.ts/formatAsPrice] No current currency was set, using the default which is EUR!"
    );
  }
  return new Intl.NumberFormat(void 0, {
    style: "currency",
    currency: currentCurrency ?? GraphQL.Currency.Eur,
    currencyDisplay: "narrowSymbol"
  }).format(price);
}
function toNextOccurenceFromRecurrence(recurrences, options = { includeCurrent: false, now: dateTime_exports.hotelDateNow() }) {
  if (!recurrences) return null;
  const recs = recurrences.map(function(recFragment) {
    return getFragment(SharedAPI.recurrence.RECURRENCE_FRAGMENT, recFragment);
  });
  const rrules = recurrencesToRRule(recs);
  if (options.includeCurrent) {
    return currentOrNextOccurence(rrules, options.now);
  }
  return nextOccurence(rrules, options.now);
}
function toGoodieType(goodie) {
  switch (goodie.__typename) {
    case "Default":
      return goodie.default;
    case "Event":
      return goodie.event;
    default:
      return null;
  }
}
function toCurrentAvailabilityFromGoodieFragment(goodie) {
  const now = dateTime_exports.hotelDateNow();
  switch (goodie.__typename) {
    case "Default":
      if (!goodie.default.openingHours) return null;
      return goodie.default.openingHours.flatMap(function(avFragment) {
        const availability = getFragment(
          SharedAPI.recurrence.RECURRENCE_FRAGMENT,
          avFragment
        );
        if (!availability) return null;
        const rrule = recurrenceToRRule(availability);
        return occurencesForDay(rrule, now);
      }).find((availability) => {
        if (!availability) return false;
        return availability.start < now && now < availability.end;
      });
    default:
      return null;
  }
}
function toNextOccurenceFromGoodieFragment(goodie, options) {
  switch (goodie.__typename) {
    case "Default":
      return toNextOccurenceFromRecurrence(goodie.default.openingHours, options);
    case "Event":
      return toNextOccurenceFromRecurrence(goodie.event.recurrences, options);
    default:
      return null;
  }
}
function toInfoFromGoodieFragment(goodie) {
  const type = toGoodieType(goodie);
  if (type) return toInfo(type);
  return null;
}
function toPriceFromGoodieFragment(goodie) {
  const type = toGoodieType(goodie);
  if (type) return toPrice(type);
  return null;
}
function isBookableFromGoodieFragment(goodie) {
  switch (goodie.__typename) {
    case "Event":
      return goodie.event.button === GraphQL.EventButtonType.Bookable;
    case "Default":
      return goodie.default.button === GraphQL.DefaultButtonType.Bookable;
    case "Message":
      return false;
  }
}

// src/index.ts
import {
  RRule as RRule2,
  Frequency,
  Weekday,
  rrulestr as rrulestr2
} from "rrule";
export {
  Frequency,
  NaiveDateTime,
  RRule2 as RRule,
  TimeUnits,
  Weekday,
  dateTime_exports as dateTime,
  goodie_exports as goodie,
  recurences_exports as recurences,
  rrulestr2 as rrulestr
};
//# sourceMappingURL=index.js.map